import type { MeasuredBrandColor } from "./onboardingAgentStream";
import type { BrandDetailsDraft, ColorSlot } from "./quickStartOnboarding";
import type { BrandResponse } from "../../types/BrandModels";

/**
 * What the website read found, turned into Brand-details fields. Shared by the
 * setup chat's step 2 (SGL-512) and the Brand wizard so both read the profile
 * the same way.
 */

/** Drop the cache-busting `v` param LogoUpload adds, so the saved URL is stable. */
export function sanitizeLogoUrlForSave(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";
  try {
    const parsed = new URL(trimmed);
    parsed.searchParams.delete("v");
    return parsed.toString();
  } catch {
    return trimmed;
  }
}

// Map measured brand colours into the form's colour slots *by role*, never
// by position (SGL-223). The backend measures what each colour does —
// identity, body text, page ground — and positional mapping threw that away,
// labelling the measured text colour "secondary" and the page background
// "accent". SGL-220 then had a palette with no colour it could safely set a
// paragraph in, which is the gap that ticket is about.
//
// The form's vocabulary (primary/secondary/accent/other) does not match the
// measurement's (primary/text/background), so the mapping is stated here
// rather than implied: identity -> primary, body text -> secondary (the
// form's slot for a second declared colour, and the one the compositor
// reaches for as a type colour), page ground -> accent.
const ROLE_TO_SLOT: Record<string, ColorSlot> = {
  primary: "primary",
  text: "secondary",
  background: "accent",
};

/** Up to four colours in form slots, or undefined when the read found none. */
export function colorsFromProfile(
  palette: unknown,
  flat: unknown
): { value: string; type: ColorSlot }[] | undefined {
  const measured = Array.isArray(palette) ? (palette as MeasuredBrandColor[]).filter((c) => c?.hex) : [];
  if (measured.length > 0) {
    return measured.slice(0, 4).map((c) => ({ value: c.hex, type: ROLE_TO_SLOT[c.role] ?? "other" }));
  }
  // Fall back to the flat list only when no roled palette arrived — an older
  // backend, or a payload that predates brand_palette. Positional mapping is
  // wrong, but dropping the customer's colours entirely is worse, and the
  // "please check these" notice covers the uncertainty either way.
  const hexes = Array.isArray(flat) ? (flat as unknown[]).filter((x): x is string => typeof x === "string" && !!x.trim()) : [];
  if (hexes.length > 0) {
    return hexes.slice(0, 4).map((hex, i) => ({
      value: hex,
      type: (["primary", "secondary", "accent", "other"] as const)[i] ?? "other",
    }));
  }
  return undefined;
}

const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");
const firstStr = (p: Record<string, unknown>, ...keys: string[]) => keys.map((k) => str(p[k])).find(Boolean) ?? "";

/**
 * Brand-details fields from a profile payload. Only non-empty fields, so a
 * later turn never blanks what an earlier one found. The website's logo goes
 * in `sourceLogoUrl` only: it is copied into our storage, never saved as is.
 */
export function detailsFromPayload(p: Record<string, unknown>): Partial<BrandDetailsDraft> {
  const out: Partial<BrandDetailsDraft> = {};
  const logo = firstStr(p, "logo_url", "logoUrl");
  if (/^https?:\/\//i.test(logo)) out.sourceLogoUrl = logo;
  const logoDark = firstStr(p, "logo_url_dark", "logoUrlDark");
  if (/^https?:\/\//i.test(logoDark)) out.sourceLogoUrlDark = logoDark;
  const colors = colorsFromProfile(p.brand_palette ?? p.brandPalette, p.brand_colors ?? p.brandColors);
  if (colors) {
    out.colors = colors;
    out.colorsDetected = p.brand_colors_detected === true;
  }
  const tagline = firstStr(p, "tagline");
  if (tagline) out.tagline = tagline;
  const voice = firstStr(p, "brand_voice", "tone", "voice");
  if (voice) out.brandVoice = voice;
  const audience = firstStr(p, "target_audience");
  if (audience) out.targetAudience = audience;
  return out;
}

/**
 * True while a Brand still needs step 2: it is ONBOARDING and its details have
 * no service area yet (the same test customer-management uses to activate it).
 * A Brand stuck in ONBOARDING whose details are complete is the SGL-513
 * backfill's job, not the owner's, so it is not sent back to setup.
 */
export function needsBrandDetails(status: string | undefined, details: BrandResponse | undefined): boolean {
  if (status !== "ONBOARDING") return false;
  const areas = details?.brand?.service_areas ?? [];
  return !areas.some((a) => typeof a === "string" && a.trim());
}
